import { Box, Typography } from "@mui/material";
import { captureException } from "@sentry/react";
import DOMPurify from "dompurify";
import React, { useEffect, useMemo, useRef } from "react";
import { OAK_FONT_FAMILY } from "util/fontStacks";

interface PreviewPaneProps {
  html: string;
  label?: string;
}

// Blocks every outside load; the email's CSS and data: images and fonts still work.
export const PREVIEW_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; img-src data: blob:; font-src data:";

const BASE_STYLES = `body { margin: 0; font-family: ${OAK_FONT_FAMILY}; line-height: 1.4; } div, p { margin: 0; padding: 0; }`;

// The sanitized email as a standalone document; its body is empty if sanitizing fails.
function previewDocument(html: string): string {
  let body = "";
  try {
    body = DOMPurify.sanitize(html, { FORCE_BODY: true });
  } catch (error) {
    captureException(error);
  }
  return `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${PREVIEW_CSP}"><style>${BASE_STYLES}</style></head><body>${body}</body></html>`;
}

// Grows the frame to its content; same-origin access is why the sandbox allows it.
function useFitFrameHeight(frameRef: React.RefObject<HTMLIFrameElement | null>): void {
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame || typeof ResizeObserver === "undefined") return;
    // scrollHeight never drops below the frame's current height, so measure the root's own box,
    // plus the horizontal scrollbar a wide email adds.
    const fit = () => {
      const root = frame.contentDocument?.documentElement;
      const view = frame.contentWindow;
      if (!root || !view) return;
      const scrollbar = view.innerHeight - root.clientHeight;
      frame.style.height = `${Math.ceil(root.getBoundingClientRect().height + scrollbar)}px`;
    };
    const observer = new ResizeObserver(fit);
    const onLoad = () => {
      observer.disconnect();
      const body = frame.contentDocument?.body;
      if (body) observer.observe(body);
      fit();
    };
    frame.addEventListener("load", onLoad);
    return () => {
      frame.removeEventListener("load", onLoad);
      observer.disconnect();
    };
  }, [frameRef]);
}

const PreviewPane: React.FC<PreviewPaneProps> = ({ html, label }) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const srcDoc = useMemo(() => previewDocument(html), [html]);
  useFitFrameHeight(frameRef);

  return (
    <Box
      sx={{
        minHeight: 200,
        border: "1px solid #ccc",
        borderRadius: 1,
        p: 1,
        background: "#fafafa",
        position: "relative",
        overflow: "visible", // allow label to float outside
      }}
    >
      {label && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            position: "absolute",
            top: -10,
            left: 11.5,
            px: 0.4,
            background: "#fafafa",
            zIndex: 2,
            fontSize: "0.85rem",
            pointerEvents: "none",
            lineHeight: 1,
          }}
        >
          {label}
        </Typography>
      )}
      <iframe
        ref={frameRef}
        title={label ?? "Email preview"}
        // No allow-scripts: email content never runs, and must never get both flags.
        sandbox="allow-same-origin"
        srcDoc={srcDoc}
        style={{ display: "block", width: "100%", border: 0 }}
      />
    </Box>
  );
};

export default PreviewPane;
